import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BRAND_DARK, BRAND_LIGHT } from "@/shared/constants.js";
import { CheckIcon, Spinner } from "@/shared/components/Icon.jsx";
import Alert from "@/shared/components/Alert.jsx";
import Loader from "@/shared/components/Loader.jsx";
import { MedMalGuardHeader } from "@/modules/Quote/components/MedMalGuardLanding.jsx";
import ThemeToggle from "@/shared/components/ThemeToggle.jsx";
import { btnOutline, btnPrimary } from "@/shared/utils/styles.js";
import { fetchInsuredOrderDetails } from "@/modules/Quote/api/quoteApi.js";
import { downloadCOI, downloadBinder } from "@/modules/Payment/utils/downloads.js";
import { readPolicyStatus, POLICY_STATUS } from "@/modules/Payment/utils/policyStatus.js";

import { useStore } from "@/shared/store/useStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";
import sessionStore from "@/shared/store/sessionStore.js";
import modalStore from "@/shared/store/modalStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import paymentOrderStore from "@/modules/Payment/store/paymentOrderStore.js";
import bindStore from "@/modules/Payment/store/bindStore.js";

export default function CompleteOrderPage() {
  const navigate = useNavigate();
  const flowSubmissionId = useStore(submissionStore, (s) => s.flowSubmissionId);
  const insuredProfile   = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated  = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const doDownload = async (key, fn) => {
    setDownloadingKey(key);
    try { await fn(); } finally { setDownloadingKey(null); }
  };

  useEffect(() => {
    if (!flowSubmissionId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchInsuredOrderDetails(flowSubmissionId)
      .then((data) => {
        if (cancelled) return;
        if (readPolicyStatus(data) !== POLICY_STATUS.POLICY_ACTIVE) {
          navigate("/dashboard");
          return;
        }
        setPolicy(data);
      })
      .catch((e) => { if (!cancelled) setError(e?.message || "Could not load policy details."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [flowSubmissionId, navigate]);

  const resetCompletionAndPaymentOrderBootstrap = () => {
    bindStore.clear();
    paymentOrderStore.clear();
  };

  const r = policy?.ratingResponse ?? null;
  const policyNumber = policy?.policynumber || policy?.policyNumber || policy?.quotenumber || policy?.quoteNumber || (flowSubmissionId ? `#${flowSubmissionId}` : "—");
  const email = policy?.contactResponse?.email || "—";
  const specialty = r?.specialtyTitle || policy?.speciality || policy?.specialty || "";
  const limits = r?.coverageLimitTitle || "";
  const practiceLocation = r?.practiceLocation || "";
  const summaryLine = [limits, specialty, practiceLocation].filter(Boolean).join(" · ") || "—";
  const premium = r?.total ?? policy?.balance ?? policy?.premium ?? policy?.annualpremium ?? null;
  const premiumRounded = premium != null ? Math.round(Number(premium)) : null;

  return (
    <>
      {/* MedMalGuard header at every viewport — collapses to logo +
          hamburger on phones, same as the landing page. */}
      <div className="app-header">
        <MedMalGuardHeader />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "12px 18px 8px", borderBottom: "1px solid #f0f0f0" }}>
        <ThemeToggle />
      </div>

      <div style={{ flex: 1, padding: "0 18px 18px", overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", background: BRAND_LIGHT,
          display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20,
          animation: "popIn 0.5s cubic-bezier(0.175,0.885,0.32,1.275)",
        }}>
          <CheckIcon size={28} />
        </div>

        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 600, color: BRAND_DARK, margin: "0 0 6px" }}>
          You're covered!
        </h2>

        <p style={{ fontSize: 13, color: "#777", lineHeight: 1.6, maxWidth: 280, margin: "0 0 20px" }}>
          <>Policy bound. Confirmation sent to <strong style={{ color: "#333" }}>{email}</strong>.</>
        </p>

        {loading && <Loader label="Loading policy details…" />}
        <Alert type="error" message={error} />

        <div style={{ background: "#f7f7f5", borderRadius: 12, padding: "14px 18px", width: "100%", maxWidth: 280 }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            <>Policy number · {policyNumber}</>
          </div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 600, color: BRAND_DARK }}>
            {premiumRounded != null
              ? <>${premiumRounded.toLocaleString()}<span style={{ fontSize: 12, color: "#999", fontFamily: "'DM Sans',sans-serif", fontWeight: 400 }}>/yr</span></>
              : "—"
            }
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{summaryLine}</div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16, width: "100%", maxWidth: 280 }}>
          <button
            onClick={() => { if (!downloadingKey) doDownload("coi", () => downloadCOI(flowSubmissionId)); }}
            style={{ ...btnOutline, flex: 1, fontSize: 12, marginTop: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: downloadingKey === "coi" ? 0.7 : 1, pointerEvents: downloadingKey ? "none" : "auto" }}
          >
            {downloadingKey === "coi" ? <><Spinner />Downloading…</> : "Download COI"}
          </button>
          <button
            onClick={() => { if (!downloadingKey) doDownload("binder", () => downloadBinder(flowSubmissionId, true, () => {})); }}
            style={{ ...btnOutline, flex: 1, fontSize: 12, marginTop: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: downloadingKey === "binder" ? 0.7 : 1, pointerEvents: downloadingKey ? "none" : "auto" }}
          >
            {downloadingKey === "binder" ? <><Spinner />Downloading…</> : "Binder"}
          </button>
        </div>

        <button
          type="button"
          onClick={() => {
            resetCompletionAndPaymentOrderBootstrap();
            sessionStore.dashView = "dashboard";
            navigate("/dashboard");
          }}
          style={{ ...btnPrimary, marginTop: 16, width: "100%", maxWidth: 280, fontSize: 13 }}
        >
          Go to Dashboard
        </button>
      </div>

      <div style={{ padding: "6px 16px", borderTop: "1px solid #f0f0f0", textAlign: "center", fontSize: 10, color: "#ccc" }}>
        SelectFirst Insurance Services · (888) 966-3881 ·{" "}
        <span onClick={() => { modalStore.showAbout = true; }} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>About</span> ·{" "}
        <span onClick={() => { modalStore.showPrivacy = true; }} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>Privacy</span> ·{" "}
        <span onClick={() => { modalStore.showTerms = true; }} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>Terms</span>
      </div>

      <style>{`@keyframes popIn{0%{transform:scale(0)}100%{transform:scale(1)}}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}
